/* Reviews management page with drag-free up/down reordering, clear card layout.
   Updated: Removed manual "Google Review Instellingen" card — score and count are now
   auto-calculated from published reviews via the get_review_stats() database function.
   Fixed review ordering by removing conflicting secondary sort and using optimistic
   local state updates instead of full refetch after reorder. */

import { useState, useEffect } from 'react';

import Breadcrumb from '@/components/admin/Breadcrumb';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Star, Trash2, CreditCard as Edit, Plus, CircleAlert as AlertCircle, CircleCheck as CheckCircle2, Eye, EyeOff, ChevronUp, ChevronDown } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface Review {
  id: string;
  name: string;
  text: string;
  stars: number;
  is_published: boolean;
  display_order: number;
  created_at: string;
}

export default function AdminReviews() {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingReview, setEditingReview] = useState<Review | null>(null);
  const [deleteReviewId, setDeleteReviewId] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [autoScore, setAutoScore] = useState<number>(0);
  const [autoCount, setAutoCount] = useState<number>(0);

  const [formData, setFormData] = useState({
    name: '',
    text: '',
    stars: 5,
    is_published: true,
  });

  useEffect(() => {
    fetchReviews();
    fetchReviewStats();
  }, []);

  const fetchReviews = async () => {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) throw error;
      setReviews(data || []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      setMessage({ type: 'error', text: 'Fout bij het ophalen van reviews' });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchReviewStats = async () => {
    try {
      const { data, error } = await supabase.rpc('get_review_stats');
      if (error) throw error;
      if (data && data.length > 0) {
        setAutoScore(parseFloat(data[0].avg_score) || 0);
        setAutoCount(parseInt(data[0].review_count) || 0);
      }
    } catch (error) {
      console.error('Error fetching review stats:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    try {
      if (editingReview) {
        const { error } = await supabase
          .from('reviews')
          .update(formData)
          .eq('id', editingReview.id);

        if (error) throw error;
        setMessage({ type: 'success', text: 'Review succesvol bijgewerkt' });
      } else {
        const { error } = await supabase
          .from('reviews')
          .insert([{ ...formData, display_order: reviews.length }]);

        if (error) throw error;
        setMessage({ type: 'success', text: 'Review succesvol toegevoegd' });
      }

      resetForm();
      fetchReviews();
      fetchReviewStats();
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error saving review:', error);
      setMessage({ type: 'error', text: 'Fout bij het opslaan van review' });
    }
  };

  const handleEdit = (review: Review) => {
    setEditingReview(review);
    setFormData({
      name: review.name,
      text: review.text,
      stars: review.stars,
      is_published: review.is_published,
    });
    setShowForm(true);
  };

  const handleDelete = async () => {
    if (!deleteReviewId) return;

    try {
      const { error } = await supabase
        .from('reviews')
        .delete()
        .eq('id', deleteReviewId);

      if (error) throw error;

      setMessage({ type: 'success', text: 'Review succesvol verwijderd' });
      fetchReviews();
      fetchReviewStats();
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error deleting review:', error);
      setMessage({ type: 'error', text: 'Fout bij het verwijderen van review' });
    } finally {
      setDeleteReviewId(null);
    }
  };

  const togglePublished = async (review: Review) => {
    try {
      const { error } = await supabase
        .from('reviews')
        .update({ is_published: !review.is_published })
        .eq('id', review.id);

      if (error) throw error;

      setReviews(prev => prev.map(r =>
        r.id === review.id ? { ...r, is_published: !r.is_published } : r
      ));
      fetchReviewStats();
      setMessage({
        type: 'success',
        text: `Review ${!review.is_published ? 'gepubliceerd' : 'verborgen'}`
      });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error toggling review:', error);
      setMessage({ type: 'error', text: 'Fout bij het wijzigen van review status' });
    }
  };

  const handleMoveOrder = async (review: Review, direction: 'up' | 'down') => {
    const currentIndex = reviews.findIndex(r => r.id === review.id);
    if (
      (direction === 'up' && currentIndex === 0) ||
      (direction === 'down' && currentIndex === reviews.length - 1)
    ) {
      return;
    }

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    const targetReview = reviews[targetIndex];

    try {
      const updates = [
        supabase.from('reviews').update({ display_order: targetReview.display_order }).eq('id', review.id),
        supabase.from('reviews').update({ display_order: review.display_order }).eq('id', targetReview.id)
      ];

      await Promise.all(updates);

      setReviews(prev => {
        const next = [...prev];
        const a = next[currentIndex];
        const b = next[targetIndex];
        next[currentIndex] = { ...b, display_order: a.display_order };
        next[targetIndex] = { ...a, display_order: b.display_order };
        return next.sort((x, y) => x.display_order - y.display_order);
      });
    } catch (error) {
      console.error('Error reordering reviews:', error);
      setMessage({ type: 'error', text: 'Fout bij het herordenen van reviews' });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      text: '',
      stars: 5,
      is_published: true,
    });
    setEditingReview(null);
    setShowForm(false);
  };

  const renderStars = (count: number, onChange?: (stars: number) => void) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-6 h-6 ${
              star <= count ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
            } ${onChange ? 'cursor-pointer hover:fill-yellow-300' : ''}`}
            onClick={() => onChange?.(star)}
          />
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-12">
        <Breadcrumb pageName="Reviews" />
        <h1 className="text-4xl font-bold text-jv-primary mb-6">Reviews</h1>
        <div className="bg-white rounded-2xl shadow-md overflow-hidden p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jv-primary mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-7xl mx-auto px-6 py-12">
        <Breadcrumb pageName="Reviews" />

        <div className="mb-8">
          <h1 className="text-4xl font-bold text-jv-primary mb-2">Reviews</h1>
          <p className="text-gray-600">Beheer klantreviews — score en aantal worden automatisch berekend</p>
        </div>

        {message && (
          <Alert
            variant={message.type === 'error' ? 'destructive' : 'default'}
            className="mb-6"
          >
            {message.type === 'success' ? (
              <CheckCircle2 className="h-4 w-4" />
            ) : (
              <AlertCircle className="h-4 w-4" />
            )}
            <AlertDescription>{message.text}</AlertDescription>
          </Alert>
        )}

        <Card className="shadow-lg mb-8">
          <CardHeader className="bg-[#fcfaf6] border-b border-[#e7e1d6]">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#e7e1d6] rounded-lg">
                <Star className="w-6 h-6 text-[#1e2b22]" />
              </div>
              <div>
                <CardTitle className="text-2xl">Review Statistieken</CardTitle>
                <CardDescription className="text-base mt-1">
                  Automatisch berekend uit gepubliceerde reviews
                </CardDescription>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-[#fcfaf6] rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-1">Gemiddelde score</p>
                <div className="flex items-center gap-2">
                  <span className="text-3xl font-bold text-[#1e2b22]">
                    {autoScore.toFixed(1).replace('.', ',')}
                  </span>
                  <span className="text-lg text-gray-500">/ 5</span>
                </div>
                <div className="flex gap-0.5 mt-2">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star
                      key={s}
                      className={`w-4 h-4 ${s <= Math.round(autoScore) ? 'fill-yellow-400 text-yellow-400' : 'fill-[#e7e1d6] text-[#e7e1d6]'}`}
                    />
                  ))}
                </div>
              </div>

              <div className="bg-[#fcfaf6] rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-1">Aantal gepubliceerde reviews</p>
                <span className="text-3xl font-bold text-[#1e2b22]">{autoCount}</span>
                <p className="text-xs text-[#5a6552] mt-2">
                  Wordt automatisch bijgewerkt wanneer je reviews toevoegt, bewerkt of verwijdert
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Klant Reviews</h2>
            <p className="text-gray-600">Beheer klantreviews die op de homepage worden weergegeven</p>
          </div>
          <Button
            onClick={() => setShowForm(!showForm)}
            className="bg-jv-primary hover:bg-jv-primary/90"
          >
            <Plus className="w-4 h-4 mr-2" />
            {showForm ? 'Annuleer' : 'Review toevoegen'}
          </Button>
        </div>

        {showForm && (
          <Card className="mb-6 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-jv-primary/10 to-white border-b">
              <CardTitle className="text-2xl">
                {editingReview ? 'Review bewerken' : 'Nieuwe review toevoegen'}
              </CardTitle>
              <CardDescription>
                {editingReview ? 'Wijzig de gegevens van deze review' : 'Voeg een nieuwe klantreview toe'}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name" className="text-base font-semibold">
                    Naam klant
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Bijv. Jan de Vries"
                    required
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="text" className="text-base font-semibold">
                    Review tekst
                  </Label>
                  <Textarea
                    id="text"
                    value={formData.text}
                    onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                    placeholder="Schrijf hier de review..."
                    required
                    rows={4}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label className="text-base font-semibold mb-2 block">
                    Sterren beoordeling
                  </Label>
                  {renderStars(formData.stars, (stars) => setFormData({ ...formData, stars }))}
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_published"
                    checked={formData.is_published}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_published: checked })}
                  />
                  <Label htmlFor="is_published" className="cursor-pointer">
                    Publiceren op website
                  </Label>
                </div>

                <div className="flex gap-3">
                  <Button type="submit" className="flex-1 bg-jv-primary hover:bg-jv-primary/90">
                    {editingReview ? 'Wijzigingen opslaan' : 'Review toevoegen'}
                  </Button>
                  <Button type="button" variant="outline" onClick={resetForm}>
                    Annuleer
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        <div className="space-y-4">
          {reviews.length === 0 ? (
            <Card className="p-12 text-center">
              <Star className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 text-lg">Nog geen reviews toegevoegd</p>
              <p className="text-gray-500 mt-2">Klik op "Review toevoegen" om te beginnen</p>
            </Card>
          ) : (
            reviews.map((review, index) => (
              <Card key={review.id} className={`shadow hover:shadow-md transition-shadow ${!review.is_published ? 'opacity-60' : ''}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="flex flex-col gap-1 pt-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleMoveOrder(review, 'up')}
                          disabled={index === 0}
                          className="h-8 w-8 p-0"
                        >
                          <ChevronUp className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleMoveOrder(review, 'down')}
                          disabled={index === reviews.length - 1}
                          className="h-8 w-8 p-0"
                        >
                          <ChevronDown className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="text-sm font-semibold text-gray-400">#{index + 1}</span>
                          <h3 className="text-xl font-bold">{review.name}</h3>
                          {review.is_published ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#e7e1d6] text-[#1e2b22] rounded text-sm">
                              <Eye className="w-3 h-3" />
                              Gepubliceerd
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-800 rounded text-sm">
                              <EyeOff className="w-3 h-3" />
                              Verborgen
                            </span>
                          )}
                        </div>
                        {renderStars(review.stars)}
                        <p className="text-gray-700 mt-3 leading-relaxed">{review.text}</p>
                        <p className="text-sm text-gray-500 mt-3">
                          {new Date(review.created_at).toLocaleDateString('nl-NL', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => togglePublished(review)}
                        title={review.is_published ? 'Verbergen' : 'Publiceren'}
                      >
                        {review.is_published ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleEdit(review)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setDeleteReviewId(review.id)}
                        className="text-[#a24a3d] hover:text-[#8a3a2f] hover:bg-[#a24a3d]/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      <AlertDialog open={!!deleteReviewId} onOpenChange={() => setDeleteReviewId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Review verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je deze review wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-[#a24a3d] hover:bg-[#8a3a2f]"
            >
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
